/**
 * Faut-il montrer la barre de mise en forme d'un texte riche ?
 *
 * Le sujet a l'air trivial et ne l'est pas : ProseMirror **ne vide pas la
 * sélection au blur**, et la barre se positionne sur le rectangle de cette
 * sélection. Une barre laissée visible alors que plus rien n'est sélectionné se
 * place donc sur un rectangle qui ne veut plus rien dire — en pratique, tout en
 * haut du cadre, ou hors de l'écran. Les deux symptômes (« elle reste »,
 * « elle apparaît trop haut ») sont la même erreur.
 *
 * D'où la règle : **pas de sélection, pas de barre**, sauf verrou explicite.
 *
 * @param {object} state
 * @param {boolean} state.hasSelection  sélection non vide dans l'éditeur
 * @param {boolean} state.isFocused     l'éditeur a le focus
 * @param {boolean} state.interacting   la souris travaille dans la barre
 * @param {number}  state.locks         verrous ouverts (popover de couleur, stepper…)
 * @returns {boolean}
 */
export function shouldShowBubbleMenu({
  hasSelection = false,
  isFocused = false,
  interacting = false,
  locks = 0,
} = {}) {
  // Un verrou = un contrôle ouvert au-dessus de la barre (palette de couleurs,
  // champ de lien). Le fermer rend la main ; le masquer sous le doigt, non.
  if (locks > 0) return true;
  if (!hasSelection) return false;
  // Cliquer dans la barre fait perdre le focus à l'éditeur : sans ce cas, la
  // barre disparaîtrait au moment où on s'en sert.
  if (interacting) return true;
  return isFocused;
}

/** Sélection ProseMirror → « il y a quelque chose de sélectionné ». */
export function hasEditorSelection(selection) {
  if (!selection) return false;
  const { empty, from, to } = selection;
  return !empty && from !== to;
}

/**
 * Faut-il refermer la barre après que l'éditeur a perdu le focus ?
 *
 * C'est une question distincte de `shouldShowBubbleMenu` : cette règle-là n'est
 * consultée que lors d'une transaction ProseMirror, et **perdre le focus n'en
 * produit aucune**. Sans fermeture explicite, la barre reste donc à l'écran.
 *
 * Tiptap a bien sa propre fermeture au blur, mais elle abandonne dès que
 * l'élément qui a reçu le focus est dans le parent de la barre — et ce parent
 * est le body : autant dire toujours.
 *
 * @param {object} state
 * @param {boolean} state.isFocused          l'éditeur a repris le focus entre-temps
 * @param {number}  state.locks              contrôles ouverts au-dessus de la barre
 * @param {boolean} state.focusInsideBar     le focus est parti dans la barre
 * @returns {boolean}
 */
export function shouldHideAfterBlur({
  isFocused = false,
  locks = 0,
  focusInsideBar = false,
} = {}) {
  if (isFocused) return false;
  if (locks > 0) return false;
  if (focusInsideBar) return false;
  return true;
}
